/**
 * KeywordsOverlay — pure helpers that drive the keyword reference overlay.
 *
 * Everything here is DERIVED from the ability definitions passed in (the
 * shared GAME_ABILITY_DEFINITIONS is the single source of truth) plus the
 * shared MINION_TYPE_* display constants. No keyword, class or level is
 * hard-coded: adding a new ability definition (new keyword, new class, new
 * level requirement, changed description) automatically shows up in the
 * overlay — the class filter buttons are the union of all definition types,
 * and the rows are sorted by level requirement.
 */

import type { AbilityDefinition } from '../../shared/models/AbilityRegistry';
import {
  MinionType,
  VALID_MINION_TYPES,
  MINION_TYPE_NAMES,
  MINION_TYPE_ICONS,
} from '../../shared/models/MinionType';

/**
 * The unique set of classes that have at least one keyword, ordered by
 * VALID_MINION_TYPES. Any type not (yet) in VALID_MINION_TYPES is appended
 * alphabetically, so a keyword for a brand-new class still gets a button.
 *
 * @param definitions The ability definitions to derive the classes from.
 * @returns Ordered array of class type strings (e.g. ['warrior', 'mage']).
 */
export function keywordClasses(definitions: readonly AbilityDefinition[]): string[] {
  const present = new Set<string>();
  for (const def of definitions) {
    for (const t of def.types) present.add(t);
  }
  const known = VALID_MINION_TYPES.filter((t) => present.has(t));
  const extra = [...present]
    .filter((t) => !(VALID_MINION_TYPES as readonly string[]).includes(t))
    .sort((a, b) => a.localeCompare(b));
  return [...known, ...extra];
}

/**
 * Definitions sorted by level requirement (ascending), so the overlay reads
 * "available at level 1" → "available at max level". The sort is stable:
 * keywords with the same level keep their definition (authoring) order.
 *
 * @param definitions The ability definitions to sort.
 * @returns A NEW sorted array (the input is not mutated).
 */
export function sortKeywordsByLevel(
  definitions: readonly AbilityDefinition[]
): AbilityDefinition[] {
  return definitions
    .map((def, index) => ({ def, index }))
    .sort((a, b) =>
      a.def.availableAtLevel - b.def.availableAtLevel || a.index - b.index
    )
    .map((entry) => entry.def);
}

/**
 * Narrows the (level-sorted) definitions down to one class: a keyword
 * matches when its `types` array contains the class. A null/undefined
 * filter returns everything.
 *
 * @param definitions The ability definitions to filter.
 * @param classFilter The class to keep, or null/undefined for all.
 * @returns A NEW array (the input is not mutated).
 */
export function filterKeywordsByClass(
  definitions: readonly AbilityDefinition[],
  classFilter: string | null | undefined
): AbilityDefinition[] {
  if (!classFilter) return [...definitions];
  return definitions.filter((def) => def.types.includes(classFilter));
}

/**
 * Level-requirement label for a keyword:
 * - availableAtLevel 0 or 1 → "Lv.1+ (any level)" (level is 1-based:
 *   level = upgradeCount + 1, so 0/1 both mean "from the first level")
 * - n → "Lv.n+"
 */
export function formatLevelRequirement(availableAtLevel: number): string {
  return availableAtLevel <= 1 ? 'Lv.1+ (any level)' : `Lv.${availableAtLevel}+`;
}

/**
 * Display label for a class filter button: shared icon + display name.
 * Unknown types degrade to the raw type string (no crash, no "❓").
 */
export function keywordClassLabel(type: string): string {
  const key = type as MinionType;
  const icon = MINION_TYPE_ICONS[key];
  const name = MINION_TYPE_NAMES[key] ?? type;
  return icon ? `${icon} ${name}` : name;
}
